import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { ArrowRight, ArrowsClockwise, Atom, Books, Flask, Pulse } from "@phosphor-icons/react";
import { toast } from "sonner";

const LAB_IMG = "https://images.pexels.com/photos/8940480/pexels-photo-8940480.jpeg";

export default function Dashboard() {
    const [status, setStatus] = useState(null);
    const [recentFormulas, setRecentFormulas] = useState([]);
    const [compoundsSample, setCompoundsSample] = useState([]);
    const [refreshing, setRefreshing] = useState(false);

    const load = async () => {
        const [s, f, c] = await Promise.all([
            api.get("/corpus/status"),
            api.get("/formulas"),
            api.get("/compounds"),
        ]);
        setStatus(s.data);
        setRecentFormulas(f.data.slice(0, 4));
        setCompoundsSample(c.data.slice(0, 6));
    };

    useEffect(() => { load(); }, []);

    const triggerRefresh = async () => {
        setRefreshing(true);
        try {
            await api.post("/corpus/refresh");
            toast.success("Actualización del corpus lanzada — puede tardar varios minutos");
            setTimeout(load, 1500);
        } catch (e) {
            toast.error("No se pudo lanzar la actualización");
        } finally {
            setRefreshing(false);
        }
    };

    const last = status?.last_event;
    return (
        <div className="fade-up">
            <PageHeader
                eyebrow={`Dashboard · ${new Date().toLocaleDateString("es-ES", { day:"numeric", month:"long", year:"numeric"})}`}
                title={<>Formulación médico-estética <em className="text-[var(--sage)]">basada en evidencia.</em></>}
                lead="Síntesis viva de PubMed, Europe PMC y ClinicalTrials.gov. Cada 15 días el corpus se reevalúa automáticamente; cada fórmula aquí propuesta cita su fuente."
                actions={
                    <button
                        onClick={triggerRefresh}
                        disabled={refreshing || status?.is_running}
                        className="px-4 py-2.5 border hairline bg-white text-sm hover:bg-[var(--cream)] transition-colors flex items-center gap-2 disabled:opacity-50"
                        data-testid="dashboard-refresh-corpus-button"
                    >
                        <ArrowsClockwise size={16} className={refreshing ? "animate-spin" : ""} />
                        {status?.is_running ? "Actualizando…" : "Actualizar corpus"}
                    </button>
                }
            />

            <div className="px-8 md:px-12 py-10 max-w-6xl">
                {/* Stats row */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-px bg-[var(--line)] border hairline" data-testid="dashboard-stats">
                    <Stat label="Principios activos" value={status?.compounds_count ?? "—"} icon={Atom} delay={1}/>
                    <Stat label="Estudios indexados" value={status?.evidence_total ?? "—"} icon={Books} delay={2}/>
                    <Stat label="Fórmulas generadas" value={recentFormulas.length ? recentFormulas.length + (recentFormulas.length === 4 ? "+" : "") : "—"} icon={Flask} delay={3}/>
                    <Stat label="Última actualización" value={last?.finished_at ? new Date(last.finished_at).toLocaleDateString("es-ES",{day:"2-digit", month:"short"}) : "pendiente"} icon={Pulse} delay={4}/>
                </div>

                {/* Editorial intro */}
                <div className="grid lg:grid-cols-[1.2fr_1fr] gap-10 mt-16">
                    <article className="prose-editorial fade-up fade-up-2">
                        <div className="label-eyebrow mb-3">Manifiesto</div>
                        <p className="font-display text-2xl leading-[1.5] dropcap">
                            Ningún suplemento debería prescribirse sin referencia clínica trazable. Esta plataforma no inventa fórmulas: las construye sobre evidencia revisada cada 15 días, respetando límites toxicológicos, normativa UE y RD 1487/2009, y verificando sinergias entre 2-3 principios activos.
                        </p>
                        <div className="mt-8 flex gap-3">
                            <Link to="/formulas/nuevo" className="btn-primary inline-flex items-center gap-2" data-testid="dashboard-new-formula-cta">
                                <Flask size={16} weight="bold" /> Generar fórmula
                            </Link>
                            <Link to="/compuestos" className="px-5 py-2.5 border hairline text-sm bg-white hover:bg-[var(--cream)] transition-colors flex items-center gap-2" data-testid="dashboard-explore-compounds-cta">
                                Explorar principios activos <ArrowRight size={14} />
                            </Link>
                        </div>
                    </article>
                    <figure className="fade-up fade-up-3">
                        <img src={LAB_IMG} alt="laboratorio" className="w-full h-72 object-cover grayscale" />
                        <figcaption className="mt-3 label-eyebrow text-[var(--ink-3)]">Ingesta científica · Fuentes oficiales</figcaption>
                    </figure>
                </div>

                {/* Two columns */}
                <div className="grid lg:grid-cols-2 gap-10 mt-20">
                    <section className="fade-up fade-up-3" data-testid="recent-formulas-section">
                        <div className="flex items-end justify-between mb-5">
                            <div>
                                <div className="label-eyebrow mb-1">Fórmulas recientes</div>
                                <h3 className="font-display text-2xl tracking-tight">Diseños del laboratorio</h3>
                            </div>
                            <Link to="/formulas" className="text-sm text-[var(--ink-2)] hover:text-[var(--ink)] underline underline-offset-4">Ver todas →</Link>
                        </div>
                        <div className="border hairline bg-white">
                            {recentFormulas.length === 0 && (
                                <div className="p-8 text-center text-sm text-[var(--ink-3)]">
                                    Sin fórmulas todavía. <Link className="text-[var(--sage)] underline" to="/formulas/nuevo">Generar la primera →</Link>
                                </div>
                            )}
                            {recentFormulas.map((f) => (
                                <Link
                                    key={f.id}
                                    to={`/formulas/${f.id}`}
                                    className="block p-5 border-b hairline last:border-b-0 hover:bg-[var(--cream)] transition-colors"
                                    data-testid={`formula-card-${f.id}`}
                                >
                                    <div className="flex items-center justify-between gap-4">
                                        <div className="min-w-0">
                                            <div className="font-display text-lg leading-tight truncate">{f.title}</div>
                                            <div className="text-xs text-[var(--ink-2)] mt-1">{f.objective} · {f.components?.length || 0} principios activos</div>
                                        </div>
                                        <span className={`citation-badge ${f.status === "approved" ? "bg-[var(--sage)] text-[var(--bone)] border-[var(--sage)]" : ""}`}>
                                            {f.status === "approved" ? "Aprobada" : "Borrador"}
                                        </span>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </section>

                    <section className="fade-up fade-up-4" data-testid="compounds-sample-section">
                        <div className="flex items-end justify-between mb-5">
                            <div>
                                <div className="label-eyebrow mb-1">Catálogo</div>
                                <h3 className="font-display text-2xl tracking-tight">Principios activos destacados</h3>
                            </div>
                            <Link to="/compuestos" className="text-sm text-[var(--ink-2)] hover:text-[var(--ink)] underline underline-offset-4">Ver catálogo →</Link>
                        </div>
                        <div className="border hairline bg-white">
                            {compoundsSample.map((c) => (
                                <Link
                                    key={c.slug}
                                    to={`/compuestos/${c.slug}`}
                                    className="block p-4 border-b hairline last:border-b-0 hover:bg-[var(--cream)] flex items-center justify-between gap-4"
                                    data-testid={`compound-card-${c.slug}`}
                                >
                                    <div className="min-w-0">
                                        <div className="font-medium truncate">{c.name}</div>
                                        <div className="text-xs text-[var(--ink-2)] truncate">{(c.indications || []).slice(0, 3).join(" · ")}</div>
                                    </div>
                                    <div className="text-xs mono text-[var(--ink-3)] flex-shrink-0">{(c.evidence || []).length} ref.</div>
                                </Link>
                            ))}
                        </div>
                    </section>
                </div>

                {/* Corpus banner */}
                <div className="mt-20 p-6 md:p-8 border hairline bg-[var(--cream)] flex flex-col md:flex-row items-start md:items-center justify-between gap-6 fade-up fade-up-5">
                    <div>
                        <div className="label-eyebrow mb-2 text-[var(--sage-2)]">Corpus científico</div>
                        <h3 className="font-display text-2xl tracking-tight">
                            {last?.status === "success"
                                ? `Última ingesta: ${new Date(last.finished_at).toLocaleString("es-ES")} · ${last.new_evidence_count} nuevas referencias`
                                : last?.status === "running"
                                ? "Ingesta en curso…"
                                : "Aún no se ha completado la primera ingesta"}
                        </h3>
                        <p className="text-sm text-[var(--ink-2)] mt-2 max-w-2xl">
                            Próxima revisión automática en {15} días. Fuentes: PubMed · Europe PMC · ClinicalTrials.gov
                        </p>
                    </div>
                    <Link to="/corpus" className="px-5 py-2.5 border hairline bg-white text-sm hover:bg-white/70 inline-flex items-center gap-2" data-testid="dashboard-corpus-link">
                        Ver registro <ArrowRight size={14} />
                    </Link>
                </div>
            </div>
        </div>
    );
}

function Stat({ label, value, icon: Icon, delay }) {
    return (
        <div className={`bg-white p-6 fade-up fade-up-${delay}`}>
            <div className="flex items-center gap-2 label-eyebrow mb-3">
                <Icon size={14} weight="duotone" />
                <span>{label}</span>
            </div>
            <div className="stat-num">{value}</div>
        </div>
    );
}
